import React, { useEffect, useState } from "react";
import { ArrowRightIcon } from "@phosphor-icons/react";
import styled, { keyframes } from "styled-components";
import { colors, spacing } from "../../../theme-v2";
import { radius } from "../../../theme/radius";

const DEFAULT_TTL_MS = 5000;

export interface AddOnReadyToastItem {
  id: number;
  /** Fully-resolved message (the consumer translates it). */
  message: React.ReactNode;
}

export interface AddOnReadyToastsProps {
  items: AddOnReadyToastItem[];
  /** Clicking a toast opens its add-on (the consumer selects the tab). */
  onOpen: (id: number) => void;
  onDismiss: (id: number) => void;
  /** Auto-dismiss delay per toast. Defaults to 5000ms. */
  ttlMs?: number;
}

/**
 * A stack of clickable "add-on is ready" toasts for the experience-page
 * Knowledge Hub. Presentational only — the consumer supplies resolved message
 * nodes and handles open/dismiss (typically: open selects that add-on's tab).
 * Each toast auto-dismisses after `ttlMs`.
 */
export const AddOnReadyToasts: React.FC<AddOnReadyToastsProps> = ({
  items,
  onOpen,
  onDismiss,
  ttlMs = DEFAULT_TTL_MS,
}) => {
  if (items.length === 0) return null;
  return (
    <StyledToastStack>
      {items.map((item) => (
        <ReadyToastRow
          key={item.id}
          message={item.message}
          ttlMs={ttlMs}
          onOpen={() => onOpen(item.id)}
          onRemove={() => onDismiss(item.id)}
        />
      ))}
    </StyledToastStack>
  );
};

export default AddOnReadyToasts;

const ReadyToastRow: React.FC<{
  message: React.ReactNode;
  ttlMs: number;
  onOpen: () => void;
  onRemove: () => void;
}> = ({ message, ttlMs, onOpen, onRemove }) => {
  const [leaving, setLeaving] = useState(false);

  // Auto-dismiss after the TTL (pinned to mount so re-renders don't reset it).
  useEffect(() => {
    const id = globalThis.setTimeout(() => setLeaving(true), ttlMs);
    return () => globalThis.clearTimeout(id);
  }, [ttlMs]);

  return (
    <StyledToastCard
      $leaving={leaving}
      onClick={() => {
        onOpen();
        setLeaving(true);
      }}
      onAnimationEnd={() => {
        // Inline handler → always the latest onRemove; the leaving animation
        // completing is what actually removes the toast.
        if (leaving) onRemove();
      }}
    >
      <StyledToastDot />
      <StyledToastText>{message}</StyledToastText>
      <ArrowRightIcon
        size={18}
        weight="bold"
        style={{ opacity: 0.7, flexShrink: 0 }}
      />
    </StyledToastCard>
  );
};

// ─── Styled components ───

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(8px); }
`;

const StyledToastStack = styled.div`
  position: fixed;
  bottom: ${spacing.lg}px;
  right: ${spacing.lg}px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  z-index: 1000;
`;

const StyledToastCard = styled.div<{ $leaving: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm}px;
  min-width: 240px;
  padding: ${spacing.sm}px ${spacing.md}px;
  border-radius: ${radius.box}px;
  background: ${colors.text.primary};
  color: ${colors.surface.base};
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: ${({ $leaving }) => ($leaving ? slideOut : slideIn)} 200ms ease
    forwards;
`;

const StyledToastDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: ${radius.circle};
  background: ${colors.accent.base};
`;

const StyledToastText = styled.span`
  flex: 1;
  font-size: 14px;
`;
